import React, { useMemo } from 'react';
import { Checkbox, ListItemText, Autocomplete, TextField, Box, type Theme } from '@mui/material';
import { type MRT_ColumnDef } from 'material-react-table';
import type { AflDailyRow } from './columns';

type FacetEntry = { value: string; count?: number };

export interface FacetsState {
  lineType?: FacetEntry[];
  linePay?: FacetEntry[];
  category?: FacetEntry[];
  serviceType?: FacetEntry[];
  manufacturer?: FacetEntry[];
  transCode?: FacetEntry[];
  tansGroup?: FacetEntry[];
  specialOrder?: FacetEntry[];
  store?: FacetEntry[];
  opCode?: FacetEntry[];
  coveredLine?: FacetEntry[];
  coveredPart?: FacetEntry[];
  t1Label?: FacetEntry[];
  typeCode?: FacetEntry[];
}

interface UseDynamicColumnsOptions {
  baseColumns: MRT_ColumnDef<AflDailyRow>[];
  facets: FacetsState;
  theme: Theme;
  setGlobalFilter: (value: string) => void;
  setPagination: React.Dispatch<React.SetStateAction<{ pageIndex: number; pageSize: number }>>;
}

/**
 * Enhances base column definitions with server-side faceted filter UIs.
 * Extracted from Reports.tsx to reduce component size.
 */
export function useDynamicColumns({
  baseColumns,
  facets,
  theme,
  setGlobalFilter,
  setPagination,
}: UseDynamicColumnsOptions): MRT_ColumnDef<AflDailyRow>[] {
  return useMemo(() => {
    const lineTypeOptions = facets.lineType?.length
      ? facets.lineType.map((f) => f.value)
      : ['A', 'L', 'labor', '', 'I', 'W', 'MR'];
    const linePayOptions = facets.linePay?.length
      ? facets.linePay.map((f) => f.value)
      : ['C', 'I', 'W', 'MR', '', 'CP', 'TT'];
    const opCodeValues = facets.opCode?.length ? facets.opCode.map((f) => f.value) : [];
    const categoryValues = facets.category?.length ? facets.category.map((f) => f.value) : [];
    const serviceTypeValues = facets.serviceType?.length
      ? facets.serviceType.map((f) => f.value)
      : [];
    const manufacturerValues = facets.manufacturer?.length
      ? facets.manufacturer.map((f) => f.value)
      : [];
    const transCodeValues = facets.transCode?.length
      ? facets.transCode.map((f) => f.value)
      : ['CP', ''];
    const tansGroupValues = facets.tansGroup?.length ? facets.tansGroup.map((f) => f.value) : [];
    const storeValues = facets.store?.length ? facets.store.map((f) => f.value) : [];
    const t1LabelValues = facets.t1Label?.length
      ? facets.t1Label.map((f) => f.value)
      : ['business', 'individual', 'internal', 'voided', ''];
    const typeCodeValues = facets.typeCode?.length
      ? facets.typeCode.map((f) => f.value)
      : ['C', 'I', ''];

    return baseColumns.map((col: any) => {
      const makeFilter =
        (opts: Array<{ value: string; count?: number }>) =>
        ({ column }: any) => {
          const valueArr: string[] = Array.isArray(column.getFilterValue())
            ? (column.getFilterValue() as string[])
            : [];
          const options = opts.map((o) => ({
            value: o.value,
            label: (o.value || '(blank)') + (o.count != null ? ` (${o.count})` : ''),
          }));
          const selected = options.filter((o) => valueArr.includes(o.value));
          return (
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={options}
              openOnFocus
              ListboxProps={{ sx: { maxHeight: 320 } }}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              getOptionLabel={(o) => o.label}
              value={selected}
              onChange={(_, newValue) => column.setFilterValue(newValue.map((v: any) => v.value))}
              renderOption={(props, option, { selected }) => {
                const { key, ...otherProps } = props;
                return (
                  <li
                    key={key}
                    {...otherProps}
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <Checkbox checked={selected} size="small" />
                    <ListItemText
                      primary={option.label}
                      primaryTypographyProps={{ noWrap: false }}
                    />
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={`Filter by ${String(column.columnDef.header || column.id).replace(/\n/g, ' ')}`}
                  size="small"
                  sx={{ minWidth: 320 }}
                />
              )}
            />
          );
        };

      // RO Number: clickable to filter via global search (ro: prefix)
      if (col?.accessorKey === 'RO Number') {
        return {
          ...col,
          Cell: ({ cell, table }: any) => {
            const ro = String(cell.getValue?.() ?? '');
            if (!ro) return '';
            return (
              <span
                role="button"
                title={`Filter by RO ${ro}`}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  const searchTerm = `ro:${ro}`;
                  if (table?.setGlobalFilter) {
                    table.setGlobalFilter(searchTerm);
                  } else {
                    setGlobalFilter(searchTerm);
                  }
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
                style={{
                  cursor: 'pointer',
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                }}
              >
                {ro}
              </span>
            );
          },
        };
      }

      // Last Name: force high-contrast by theme mode
      if (col?.accessorKey === 'Last Name') {
        return {
          ...col,
          Cell: ({ cell }: any) => (
            <Box
              component="span"
              sx={{
                color: theme.palette.mode === 'dark' ? '#FFFFFF !important' : '#111111 !important',
                fontWeight: 'inherit',
              }}
            >
              {String(cell.getValue?.() ?? '')}
            </Box>
          ),
        };
      }

      // Faceted filter columns
      const facetedMap: Record<string, { options: string[]; facetData?: FacetEntry[] }> = {
        'Line Type': { options: lineTypeOptions, facetData: facets.lineType },
        'Line Pay Method': { options: linePayOptions, facetData: facets.linePay },
        Category: { options: categoryValues, facetData: facets.category },
        'Service Type': { options: serviceTypeValues, facetData: facets.serviceType },
        Manufacturer: { options: manufacturerValues, facetData: facets.manufacturer },
        Store: { options: storeValues, facetData: facets.store },
        'T1 Label': { options: t1LabelValues, facetData: facets.t1Label },
        'Type Code': { options: typeCodeValues, facetData: facets.typeCode },
      };

      const facetConfig = facetedMap[col?.accessorKey as string];
      if (facetConfig) {
        const data = facetConfig.facetData?.length
          ? facetConfig.facetData
          : facetConfig.options.map((v) => ({ value: v }));
        return {
          ...col,
          filterVariant: 'multi-select',
          filterSelectOptions: facetConfig.options,
          Filter: makeFilter(data),
        };
      }

      // Columns with count-based labels (Op Code, TransCode, TansGroup)
      const countBasedMap: Record<string, FacetEntry[] | undefined> = {
        'Op Code': facets.opCode,
        TransCode: facets.transCode,
        TansGroup: facets.tansGroup,
      };
      const countBasedFallbacks: Record<string, string[]> = {
        'Op Code': opCodeValues,
        TransCode: transCodeValues,
        TansGroup: tansGroupValues,
      };

      const countFacet = countBasedMap[col?.accessorKey as string];
      if (col?.accessorKey && col.accessorKey in countBasedMap) {
        const fallback = countBasedFallbacks[col.accessorKey];
        const options = countFacet?.length
          ? countFacet
          : fallback.map((v) => ({ value: v, count: undefined }));
        return {
          ...col,
          filterVariant: 'multi-select',
          filterSelectOptions: options.map((opt) => ({
            label: (opt.value || '(blank)') + (opt.count ? ` (${opt.count})` : ''),
            value: opt.value,
          })),
          Filter: makeFilter(options),
        };
      }

      // Coverage boolean columns (coveredLine, coveredPart)
      if (col?.accessorKey === 'coveredLine' || col?.accessorKey === 'coveredPart') {
        const coverageFacets =
          (col.accessorKey === 'coveredLine' ? facets.coveredLine : facets.coveredPart) || [
            { value: 'true', count: 0 },
            { value: 'false', count: 0 },
            { value: '', count: 0 },
          ];
        const label = col.accessorKey === 'coveredLine' ? 'Covered Line' : 'Covered Part';
        return {
          ...col,
          filterVariant: 'multi-select',
          filterSelectOptions: ['true', 'false', ''],
          Filter: ({ column }: any) => {
            const valueArr: string[] = Array.isArray(column.getFilterValue())
              ? (column.getFilterValue() as string[])
              : [];
            const options = coverageFacets.map((o) => ({
              value: o.value,
              label:
                (o.value === 'true'
                  ? 'Covered'
                  : o.value === 'false'
                    ? 'Not Covered'
                    : '(blank)') + ` (${o.count})`,
            }));
            const selected = options.filter((o) => valueArr.includes(o.value));
            return (
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={options}
                openOnFocus
                ListboxProps={{ sx: { maxHeight: 320 } }}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                getOptionLabel={(o) => o.label}
                value={selected}
                onChange={(_, newValue) =>
                  column.setFilterValue(newValue.map((v: any) => v.value))
                }
                renderOption={(props, option, { selected }) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li
                      key={key}
                      {...otherProps}
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <Checkbox checked={selected} size="small" />
                      <ListItemText
                        primary={option.label}
                        primaryTypographyProps={{ noWrap: false }}
                      />
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Filter by ${label}`}
                    size="small"
                    sx={{ minWidth: 320 }}
                  />
                )}
              />
            );
          },
        };
      }

      return col;
    });
  }, [baseColumns, facets, theme, setGlobalFilter, setPagination]);
}
